import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useTenant } from '../../contexts/TenantContext';

// Team building messages (reused from production QR code)
const TEAM_MESSAGES = [
    "Outstanding work! You're crushing it today!",
    "Precision is your superpower! Keep it up!",
    "Another perfect count! You're on fire!",
    "Your attention to detail is unmatched!",
    "Inventory expert at work!",
    "You make counting look easy!",
    "Accuracy champion! Well done!",
    "You're the reason our inventory stays perfect!",
    "Master counter! Impressive work!",
    "Your dedication shows in every count!",
    "Flawless execution! Amazing job!",
    "Counting hero of the day!",
    "You've got this down to a science!",
    "Inventory ninja strikes again!",
    "Perfect precision! Keep shining!",
    "You're making a huge difference!",
    "Rock star performance!",
    "Your work ethic is inspiring!",
    "Inventory excellence achieved!",
    "You're the backbone of our operations!",
    "Legendary counting skills!",
    "Absolutely phenomenal work!",
    "You're raising the bar!",
    "Excellence personified!",
    "Your contributions matter immensely!"
];

interface Material {
    id: string;
    name: string;
    itemNumber: number;
    currentStock: number;
    unit: string;
}

const InventoryCountEntry: React.FC = () => {
    // OS-INV-04: tenant-aware API base. Defaults to Made.bb (/api/madebb) when
    // no TenantProvider wraps this component (existing Made.bb count route).
    const { apiBase } = useTenant();
    const { sessionId } = useParams<{ sessionId: string }>();
    const [itemNumber, setItemNumber] = useState('');
    const [actualCount, setActualCount] = useState('');
    const [material, setMaterial] = useState<Material | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionValid, setSessionValid] = useState(true);
    const [sessionExpiry, setSessionExpiry] = useState<string>('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // MADE-290: ref for the item number field so we can re-focus it after a
    // successful count submission (autoFocus alone only fires on first mount).
    const itemNumberRef = useRef<HTMLInputElement>(null);

    const focusItemNumber = useCallback(() => {
        // setTimeout 150ms: requestAnimationFrame is too fast for Android Chrome —
        // the browser hasn't finished its own layout pass when rAF fires on mobile.
        // 150ms is reliably after the paint cycle without being perceptible to staff.
        setTimeout(() => itemNumberRef.current?.focus(), 150);
    }, []);

    // Re-focus item number field whenever the form returns to entry state:
    // covers initial mount AND the post-success reset cycle.
    useEffect(() => {
        if (!material && !showSuccess && !loading) {
            focusItemNumber();
        }
    }, [material, showSuccess, loading, focusItemNumber]);

    useEffect(() => {
        // Verify session is valid
        const verifySession = async () => {
            try {
                // Public mobile count-entry: these endpoints are public-but-tenant-scoped
                // on BOTH tenants (Made.bb + OS, Option A), scanned on unauthenticated
                // phones. Use plain fetch — authFetch throws on writes when no token.
                const response = await fetch(`${apiBase}/count-sessions/${sessionId}`);
                if (!response.ok) {
                    setSessionValid(false);
                    const data = await response.json();
                    setError(data.error || 'Invalid or expired session');
                } else {
                    const data = await response.json();
                    setSessionExpiry(new Date(data.session.expiresAt).toLocaleDateString());
                }
            } catch (err) {
                setSessionValid(false);
                setError('Failed to verify count session');
            }
        };

        verifySession();
    }, [sessionId, apiBase]);

    const handleItemNumberSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${apiBase}/count-sessions/${sessionId}/materials/${itemNumber}`);

            if (!response.ok) {
                const data = await response.json();
                setError(data.error || 'Material not found');
                setMaterial(null);
            } else {
                const data = await response.json();
                setMaterial(data.material);
                setError('');
            }
        } catch (err) {
            setError('Failed to fetch material details');
            setMaterial(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCountSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${apiBase}/count-sessions/${sessionId}/count`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    itemNumber: parseInt(itemNumber),
                    actualCount: parseFloat(actualCount)
                })
            });

            if (!response.ok) {
                const data = await response.json();
                setError(data.error || 'Failed to submit count');
            } else {
                // Show random success message
                const randomMessage = TEAM_MESSAGES[Math.floor(Math.random() * TEAM_MESSAGES.length)];
                setSuccessMessage(randomMessage);
                setShowSuccess(true);

                // Reset after 3 seconds; focusItemNumber fires via the useEffect
                // above once material/showSuccess return to neutral state.
                setTimeout(() => {
                    setShowSuccess(false);
                    setItemNumber('');
                    setActualCount('');
                    setMaterial(null);
                }, 3000);
            }
        } catch (err) {
            setError('Failed to submit count');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setItemNumber('');
        setActualCount('');
        setMaterial(null);
        setError('');
    };

    if (!sessionValid) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                backgroundColor: '#f5f5f5'
            }}>
                <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        This count session may have expired or is invalid. Please contact your supervisor for a new count sheet.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    if (showSuccess) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                backgroundColor: '#4caf50'
            }}>
                <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#4caf50' }}>
                        Count Recorded!
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {successMessage}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Returning to item entry...
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: '#f5f5f5',
            p: 2
        }}>
            <Paper sx={{ maxWidth: 500, mx: 'auto', mt: 4, p: 3 }}>
                {/* Header */}
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                    Inventory Count
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                    Session expires: {sessionExpiry}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* Item Number Entry */}
                {!material && (
                    <form onSubmit={handleItemNumberSubmit}>
                        <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Step 1: Enter Item Number
                        </Typography>
                        <TextField
                            fullWidth
                            label="6-Digit Item Number"
                            value={itemNumber}
                            onChange={(e) => setItemNumber(e.target.value)}
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoFocus
                            required
                            sx={{ mb: 2 }}
                            inputRef={itemNumberRef}
                            inputProps={{
                                maxLength: 6,
                                min: 100000,
                                max: 999999
                            }}
                            // MADE-290: scanner wedge sends Enter after barcode;
                            // trigger lookup immediately when field holds a 6-digit value.
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && itemNumber.length === 6) {
                                    e.preventDefault();
                                    handleItemNumberSubmit(e as unknown as React.FormEvent);
                                }
                            }}
                        />
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={loading || itemNumber.length !== 6}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Look Up Item'}
                        </Button>
                    </form>
                )}

                {/* Count Entry */}
                {material && !loading && (
                    <>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Item #{material.itemNumber.toString().padStart(6, '0')}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {material.name}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                System Count: {material.currentStock} {material.unit}
                            </Typography>
                        </Box>

                        <form onSubmit={handleCountSubmit}>
                            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Step 2: Enter Actual Count
                            </Typography>
                            <TextField
                                fullWidth
                                label={`Actual Count (${material.unit})`}
                                value={actualCount}
                                onChange={(e) => setActualCount(e.target.value)}
                                type="number"
                                inputMode="decimal"
                                autoFocus
                                required
                                sx={{ mb: 2 }}
                                inputProps={{
                                    step: material.unit === 'yards' || material.unit === 'kg' ? '0.1' : '1',
                                    min: 0
                                }}
                            />
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleReset}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading || !actualCount}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Submit Count'}
                                </Button>
                            </Box>
                        </form>
                    </>
                )}
            </Paper>

            {/* Made.bb Branding */}
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 3,
                    color: 'text.secondary'
                }}
            >
                Made.bb Inventory Management
            </Typography>
        </Box>
    );
};

export default InventoryCountEntry;
